import { Editor } from '@tiptap/core';
import { Transaction } from '@tiptap/pm/state';
import { Node as PmNode } from '@tiptap/pm/model';
import { CellSelection, TableMap } from '@tiptap/pm/tables';
import { TBorderStyle, TBorderWeight, DEFAULT_BORDER_STYLE, DEFAULT_BORDER_WEIGHT } from './TableNode';
import { BORDER_EDGES, TBorderEdge } from './borderAttributes';
import { MIN_RENDERABLE_DOUBLE_BORDER_WEIGHT } from './borderRenderability';

// --- Edge attribute mappings ---

const STYLE_EDGE_ATTRS: Record<TBorderEdge, string> = {
  top: 'borderTopStyle',
  right: 'borderRightStyle',
  bottom: 'borderBottomStyle',
  left: 'borderLeftStyle',
};

const WEIGHT_EDGE_ATTRS: Record<TBorderEdge, string> = {
  top: 'borderTopWeight',
  right: 'borderRightWeight',
  bottom: 'borderBottomWeight',
  left: 'borderLeftWeight',
};

const OPPOSITE_EDGE: Record<TBorderEdge, TBorderEdge> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
};

type TCellInfo = {
  pos: number;
  node: PmNode;
  top: number;
  left: number;
  bottom: number; // exclusive
  right: number; // exclusive
};

type TTableContext = {
  tr: Transaction;
  tablePos: number;
  tableNode: PmNode;
  map: TableMap;
  tableStart: number;
};

export type TBorderSelectionState = {
  hasDoubleBorder: boolean;
  style: TBorderStyle | null;
  weight: TBorderWeight | null;
};

// --- Context helper ---

/**
 * Resolve the table around the cursor, build a context object, run the
 * callback, and dispatch the transaction only when the callback signals
 * success AND the document actually changed.
 */
const withTableContext = (editor: Editor, callback: (ctx: TTableContext) => boolean): boolean => {
  const { state } = editor;
  const { tr } = state;

  const tableInfo = findTableAround(editor);
  if (!tableInfo) return false;

  const { tablePos, tableNode } = tableInfo;
  const map = TableMap.get(tableNode);
  const tableStart = tablePos + 1;

  const result = callback({ tr, tablePos, tableNode, map, tableStart });

  if (result && tr.docChanged) {
    editor.view.dispatch(tr);
  }

  return result;
};

// --- Style commands ---

/** Set border style on the entire table. Clears all per-cell style overrides. */
export const setTableBorderStyle = (editor: Editor, style: TBorderStyle) =>
  withTableContext(editor, ({ tr, tablePos, tableNode, map, tableStart }) => {
    const prevTableStyle = getTableBorderStyle(tableNode);
    const tableWeight = getTableBorderWeight(tableNode);
    const nextTableWeight =
      style === 'double'
        ? MIN_RENDERABLE_DOUBLE_BORDER_WEIGHT
        : prevTableStyle === 'double' && tableWeight === MIN_RENDERABLE_DOUBLE_BORDER_WEIGHT
          ? DEFAULT_BORDER_WEIGHT
          : tableWeight;

    tr.setNodeMarkup(tablePos, undefined, {
      ...tableNode.attrs,
      borderStyle: style === DEFAULT_BORDER_STYLE ? null : style,
      borderWeight: nextTableWeight === DEFAULT_BORDER_WEIGHT ? null : nextTableWeight,
    });

    clearCellAttrs(tr, map, tableStart, STYLE_EDGE_ATTRS);
    if (style === 'double' || prevTableStyle === 'double') {
      clearCellAttrs(tr, map, tableStart, WEIGHT_EDGE_ATTRS);
    }
    return true;
  });

/** Set border style on selected cells + neighbor sync. */
export const setCellsBorderStyle = (editor: Editor, style: TBorderStyle) =>
  withTableContext(editor, ({ tr, tableNode, map, tableStart }) => {
    const tableDefault = (tableNode.attrs.borderStyle as TBorderStyle) || DEFAULT_BORDER_STYLE;
    const normalizedValue = style === tableDefault ? null : style;

    const selectedCells = getSelectedCells(editor, map, tableStart);
    if (selectedCells.length === 0) return false;

    // Check if any selected cell currently has double style (before applying changes)
    const hadDouble = selectedCells.some((cell) =>
      BORDER_EDGES.some((edge) => getEffectiveBorderStyle(cell.node, edge, tableDefault) === 'double')
    );

    applyCellEdgeAttrsForCells(tr, map, tableStart, selectedCells, STYLE_EDGE_ATTRS, normalizedValue);

    if (style === 'double') {
      const tableWeight = getTableBorderWeight(tableNode);
      const doubleWeightValue =
        tableWeight === MIN_RENDERABLE_DOUBLE_BORDER_WEIGHT ? null : MIN_RENDERABLE_DOUBLE_BORDER_WEIGHT;
      applyCellEdgeAttrsForCells(tr, map, tableStart, selectedCells, WEIGHT_EDGE_ATTRS, doubleWeightValue);
    } else if (hadDouble) {
      applyCellEdgeAttrsForCells(tr, map, tableStart, selectedCells, WEIGHT_EDGE_ATTRS, null);
    }

    return true;
  });

// --- Weight commands ---

/** Set border weight on the entire table. Clears all per-cell weight overrides. */
export const setTableBorderWeight = (editor: Editor, weight: TBorderWeight | null) =>
  withTableContext(editor, ({ tr, tablePos, tableNode, map, tableStart }) => {
    tr.setNodeMarkup(tablePos, undefined, {
      ...tableNode.attrs,
      borderWeight: weight === DEFAULT_BORDER_WEIGHT ? null : weight,
    });

    clearCellAttrs(tr, map, tableStart, WEIGHT_EDGE_ATTRS);
    return true;
  });

/** Set border weight on selected cells + neighbor sync. */
export const setCellsBorderWeight = (editor: Editor, weight: TBorderWeight | null) =>
  withTableContext(editor, ({ tr, tableNode, map, tableStart }) => {
    const selectedCells = getSelectedCells(editor, map, tableStart);
    if (selectedCells.length === 0) return false;

    const tableWeight = getTableBorderWeight(tableNode);
    const normalizedValue = weight === tableWeight ? null : weight;
    applyCellEdgeAttrsForCells(tr, map, tableStart, selectedCells, WEIGHT_EDGE_ATTRS, normalizedValue);
    return true;
  });

// --- Compound actions ---

/** Set no-border style + clear weight. Single transaction. */
export const applyNoBorder = (editor: Editor) =>
  withTableContext(editor, ({ tr, tablePos, tableNode, map, tableStart }) => {
    const isCells = editor.state.selection instanceof CellSelection;

    if (isCells) {
      const selectedCells = getSelectedCells(editor, map, tableStart);
      if (selectedCells.length === 0) return false;

      const tableStyleDefault = (tableNode.attrs.borderStyle as TBorderStyle) || DEFAULT_BORDER_STYLE;
      const normalizedStyle = 'none' === tableStyleDefault ? null : 'none';

      const selectedPosSet = new Set(selectedCells.map((c) => c.pos));
      const updatedCells = new Map<number, Record<string, unknown>>();
      const getCellAttrs = (pos: number): Record<string, unknown> => {
        if (updatedCells.has(pos)) return updatedCells.get(pos)!;
        const node = tr.doc.nodeAt(pos);
        return node ? { ...node.attrs } : {};
      };

      for (const cell of selectedCells) {
        const attrs = getCellAttrs(cell.pos);
        for (const edge of BORDER_EDGES) {
          attrs[STYLE_EDGE_ATTRS[edge]] = normalizedStyle;
          attrs[WEIGHT_EDGE_ATTRS[edge]] = null; // Clear weight; no-border doesn't need weight
        }
        updatedCells.set(cell.pos, attrs);

        // Neighbor sync for both style and weight
        for (const edge of BORDER_EDGES) {
          const neighbors = findNeighborCells(map, tableStart, cell, edge, tr.doc);
          for (const neighbor of neighbors) {
            if (selectedPosSet.has(neighbor.pos)) continue;

            const nAttrs = getCellAttrs(neighbor.pos);
            const opposite = OPPOSITE_EDGE[edge];
            nAttrs[STYLE_EDGE_ATTRS[opposite]] = normalizedStyle;
            nAttrs[WEIGHT_EDGE_ATTRS[opposite]] = null;
            updatedCells.set(neighbor.pos, nAttrs);
          }
        }
      }

      for (const [pos, attrs] of updatedCells) {
        const node = tr.doc.nodeAt(pos);
        if (node) tr.setNodeMarkup(pos, undefined, attrs);
      }
    } else {
      // Whole table: set style=none, clear weight, clear all per-cell overrides
      tr.setNodeMarkup(tablePos, undefined, {
        ...tableNode.attrs,
        borderStyle: 'none',
        borderWeight: null,
      });
      clearCellAttrs(tr, map, tableStart, STYLE_EDGE_ATTRS);
      clearCellAttrs(tr, map, tableStart, WEIGHT_EDGE_ATTRS);
    }

    return true;
  });

/**
 * Resolve the current border state for the active command scope:
 * - table defaults when editing the whole table
 * - unified per-cell value when a CellSelection is active
 * - `null` when a multi-cell selection contains mixed values
 */
export const getBorderSelectionState = (editor: Editor): TBorderSelectionState | null => {
  const tableInfo = findTableAround(editor);
  if (!tableInfo) return null;

  const { tablePos, tableNode } = tableInfo;
  const tableStyle = getTableBorderStyle(tableNode);
  const tableWeight = getTableBorderWeight(tableNode);

  if (!(editor.state.selection instanceof CellSelection)) {
    return {
      hasDoubleBorder: tableStyle === 'double',
      style: tableStyle,
      weight: tableStyle === 'none' ? null : tableWeight,
    };
  }

  const map = TableMap.get(tableNode);
  const tableStart = tablePos + 1;
  const selectedCells = getSelectedCells(editor, map, tableStart);

  if (selectedCells.length === 0) {
    return {
      hasDoubleBorder: tableStyle === 'double',
      style: tableStyle,
      weight: tableStyle === 'none' ? null : tableWeight,
    };
  }

  const styles = new Set<TBorderStyle>();
  const weights = new Set<TBorderWeight>();

  for (const cell of selectedCells) {
    for (const edge of BORDER_EDGES) {
      const effectiveStyle = getEffectiveBorderStyle(cell.node, edge, tableStyle);
      styles.add(effectiveStyle);

      if (effectiveStyle === 'none') {
        continue;
      }

      const effectiveWeight = getEffectiveBorderWeight(cell.node, edge, tableWeight);
      weights.add(effectiveWeight);
    }
  }

  const resolvedStyle = styles.size === 1 ? Array.from(styles)[0] : null;
  const hasDoubleBorder = styles.has('double');

  if (resolvedStyle == null || resolvedStyle === 'none') {
    return { hasDoubleBorder, style: resolvedStyle, weight: null };
  }

  return {
    hasDoubleBorder,
    style: resolvedStyle,
    weight: weights.size === 1 ? Array.from(weights)[0] : null,
  };
};

// --- Shared helpers ---

const getTableBorderStyle = (tableNode: PmNode): TBorderStyle =>
  (tableNode.attrs.borderStyle as TBorderStyle) || DEFAULT_BORDER_STYLE;

const getTableBorderWeight = (tableNode: PmNode): TBorderWeight =>
  (tableNode.attrs.borderWeight as TBorderWeight) || DEFAULT_BORDER_WEIGHT;

const getEffectiveBorderStyle = (cell: PmNode, edge: TBorderEdge, tableStyle: TBorderStyle): TBorderStyle =>
  (cell.attrs[STYLE_EDGE_ATTRS[edge]] as TBorderStyle | null) || tableStyle;

const getEffectiveBorderWeight = (cell: PmNode, edge: TBorderEdge, tableWeight: TBorderWeight): TBorderWeight => {
  return (cell.attrs[WEIGHT_EDGE_ATTRS[edge]] as TBorderWeight | null) || tableWeight;
};

/** Clear per-cell edge attrs for all cells in the table. */
const clearCellAttrs = (tr: Transaction, map: TableMap, tableStart: number, edgeAttrs: Record<TBorderEdge, string>) => {
  const attrValues = Object.values(edgeAttrs);
  const seenCellOffsets = new Set<number>();

  for (let i = 0; i < map.map.length; i++) {
    const cellPos = map.map[i];
    if (seenCellOffsets.has(cellPos)) continue;
    seenCellOffsets.add(cellPos);

    const absolutePos = tableStart + cellPos;
    const cell = tr.doc.nodeAt(absolutePos);
    if (!cell) continue;

    const needsClear = attrValues.some((attr) => cell.attrs[attr] != null);
    if (!needsClear) continue;

    const newAttrs = { ...cell.attrs };
    for (const attr of attrValues) {
      newAttrs[attr] = null;
    }
    tr.setNodeMarkup(absolutePos, undefined, newAttrs);
  }
};

/** Apply a value to all 4 edges on selected cells + neighbor sync. Accepts pre-computed cells to avoid redundant getSelectedCells calls. */
const applyCellEdgeAttrsForCells = (
  tr: Transaction,
  map: TableMap,
  tableStart: number,
  selectedCells: TCellInfo[],
  edgeAttrs: Record<TBorderEdge, string>,
  value: string | null
): void => {
  const selectedPosSet = new Set(selectedCells.map((c) => c.pos));
  const updatedCells = new Map<number, Record<string, unknown>>();

  const getCellAttrs = (pos: number): Record<string, unknown> => {
    if (updatedCells.has(pos)) return updatedCells.get(pos)!;
    const node = tr.doc.nodeAt(pos);
    return node ? { ...node.attrs } : {};
  };

  for (const cell of selectedCells) {
    const attrs = getCellAttrs(cell.pos);
    for (const edge of BORDER_EDGES) {
      attrs[edgeAttrs[edge]] = value;
    }
    updatedCells.set(cell.pos, attrs);

    // Neighbor sync
    for (const edge of BORDER_EDGES) {
      const neighbors = findNeighborCells(map, tableStart, cell, edge, tr.doc);
      for (const neighbor of neighbors) {
        if (selectedPosSet.has(neighbor.pos)) continue;

        const nAttrs = getCellAttrs(neighbor.pos);
        nAttrs[edgeAttrs[OPPOSITE_EDGE[edge]]] = value;
        updatedCells.set(neighbor.pos, nAttrs);
      }
    }
  }

  for (const [pos, attrs] of updatedCells) {
    const node = tr.doc.nodeAt(pos);
    if (node) tr.setNodeMarkup(pos, undefined, attrs);
  }
};

/** Find the table node around the current selection. */
const findTableAround = (editor: Editor): { tablePos: number; tableNode: PmNode } | null => {
  const { $from } = editor.state.selection;

  for (let d = $from.depth; d >= 0; d--) {
    const node = $from.node(d);
    if (node.type.name === 'table') {
      return { tablePos: $from.before(d), tableNode: node };
    }
  }
  return null;
};

/** Compute the full rect {top, left, bottom, right} for a cell by scanning map slots. */
const computeCellRect = (
  map: TableMap,
  cellOffset: number
): { top: number; left: number; bottom: number; right: number } => {
  let top = map.height;
  let left = map.width;
  let bottom = 0;
  let right = 0;

  for (let row = 0; row < map.height; row++) {
    for (let col = 0; col < map.width; col++) {
      if (map.map[row * map.width + col] === cellOffset) {
        if (row < top) top = row;
        if (col < left) left = col;
        if (row + 1 > bottom) bottom = row + 1;
        if (col + 1 > right) right = col + 1;
      }
    }
  }

  return { top, left, bottom, right };
};

/** Get the list of selected cells (deduplicated for merged cells). */
const getSelectedCells = (editor: Editor, map: TableMap, tableStart: number): TCellInfo[] => {
  const { selection } = editor.state;
  const cells: TCellInfo[] = [];
  const seen = new Set<number>();

  if (selection instanceof CellSelection) {
    const rect = map.rectBetween(selection.$anchorCell.pos - tableStart, selection.$headCell.pos - tableStart);

    for (let row = rect.top; row < rect.bottom; row++) {
      for (let col = rect.left; col < rect.right; col++) {
        const cellOffset = map.map[row * map.width + col];
        const cellPos = tableStart + cellOffset;
        if (seen.has(cellPos)) continue;
        seen.add(cellPos);

        const node = editor.state.doc.nodeAt(cellPos);
        if (node) {
          const cellRect = computeCellRect(map, cellOffset);
          cells.push({ pos: cellPos, node, ...cellRect });
        }
      }
    }
  } else {
    const $from = selection.$from;
    for (let d = $from.depth; d >= 0; d--) {
      const node = $from.node(d);
      if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
        const pos = $from.before(d);
        const cellOffset = pos - tableStart;
        const cellIndex = map.map.indexOf(cellOffset);
        if (cellIndex >= 0) {
          const cellRect = computeCellRect(map, cellOffset);
          cells.push({ pos, node, ...cellRect });
        }
        break;
      }
    }
  }

  return cells;
};

/** Find all neighbor cells adjacent to a given edge of a (possibly merged) cell. */
const findNeighborCells = (
  map: TableMap,
  tableStart: number,
  cell: TCellInfo,
  edge: TBorderEdge,
  doc: PmNode
): { pos: number; node: PmNode }[] => {
  const cellOffset = cell.pos - tableStart;
  const neighbors: { pos: number; node: PmNode }[] = [];
  const seenOffsets = new Set<number>();

  // Determine which slots to check based on the edge
  const slots: [number, number][] = [];
  if (edge === 'top' && cell.top > 0) {
    for (let col = cell.left; col < cell.right; col++) slots.push([cell.top - 1, col]);
  } else if (edge === 'bottom' && cell.bottom < map.height) {
    for (let col = cell.left; col < cell.right; col++) slots.push([cell.bottom, col]);
  } else if (edge === 'left' && cell.left > 0) {
    for (let row = cell.top; row < cell.bottom; row++) slots.push([row, cell.left - 1]);
  } else if (edge === 'right' && cell.right < map.width) {
    for (let row = cell.top; row < cell.bottom; row++) slots.push([row, cell.right]);
  }

  for (const [row, col] of slots) {
    const neighborOffset = map.map[row * map.width + col];
    if (neighborOffset === cellOffset || seenOffsets.has(neighborOffset)) continue;
    seenOffsets.add(neighborOffset);

    const neighborPos = tableStart + neighborOffset;
    const node = doc.nodeAt(neighborPos);
    if (node) neighbors.push({ pos: neighborPos, node });
  }

  return neighbors;
};

/** Check if the current selection is a CellSelection (multi-cell). */
export const isCellSelection = (editor: Editor): boolean => editor.state.selection instanceof CellSelection;
